import {
  AccessToken,
  assertUnreachable,
  HttpStatusCode,
  TalerCorebankApi,
  TalerErrorCode,
} from "@gnu-taler/taler-util";
import {
  AsyncButton,
  RouteDefinition,
  useBankCoreApiContext,
  useNotificationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { h, VNode } from "preact";
import { useState } from "preact/hooks";
import { useSessionState } from "../hooks/session.js";
import { OperationError } from "../components/OperationError.js";
import { ConversionRateClassForm } from "./admin/ConversionRateClassForm.js";

interface Props {
  routeCancel: RouteDefinition;
  onCreated: (id: number) => void;
}
export function NewConversionRateClass({
  routeCancel,
  onCreated,
}: Props): VNode {
  const { i18n } = useTranslationContext();
  const { state: credentials } = useSessionState();
  const token =
    credentials.status !== "loggedIn" ? undefined : credentials.token;
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  const { showError } = useNotificationContext();

  const [submitData, setSubmitData] = useState<
    TalerCorebankApi.ConversionRateClassInput | undefined
  >();

  // i18n.str`create conversion rate class`,
  const create = useNotifiedOperation<
    Awaited<ReturnType<typeof api.createConversionRateClass>>,
    [AccessToken, TalerCorebankApi.ConversionRateClassInput]
  >(
    (ct, token: AccessToken, data: TalerCorebankApi.ConversionRateClassInput) =>
      api.createConversionRateClass(token, data),
    {
      onSuccess: (success) => {
        // notifyInfo(i18n.str`Conversion rate class created.`);
        onCreated(success.conversion_rate_class_id);
      },
      onFail: showError(
        i18n.str`Failed to create conversion class.`,
        (fail) => {
          switch (fail.case) {
            case HttpStatusCode.Unauthorized:
              return i18n.str`The rights to change the account are not sufficient`;
            case HttpStatusCode.Forbidden:
              return i18n.str`Wrong credentials`;
            case HttpStatusCode.NotFound:
              return i18n.str`Account not found`;
            case HttpStatusCode.NotImplemented:
              return i18n.str`Not implemented`;
            case TalerErrorCode.BANK_NAME_REUSE:
              return i18n.str`The name of the conversion is already used.`;
            default:
              assertUnreachable(fail);
          }
        },
      ),
    },
  );

  return (
    <section class="mt-2">
      <a
        href={routeCancel.url({})}
        class="inline-flex items-center text-sm font-semibold text-brand hover:underline"
      >
        <span class="mr-1" aria-hidden="true">
          ←
        </span>
        <i18n.Translate>Back to conversion settings</i18n.Translate>
      </a>
      <div class="mx-auto mt-6 max-w-3xl">
        <h1 class="text-2xl font-semibold text-brand">
          <i18n.Translate>New conversion rate class</i18n.Translate>
        </h1>
        <p class="mt-2 text-sm text-gray-600">
          <i18n.Translate>
            Create a named set of conversion terms that can be assigned to
            accounts.
          </i18n.Translate>
        </p>

        <ConversionRateClassForm onChange={setSubmitData}>
          <div class="border-t border-onBackground/10 bg-background/60 px-5 py-4 sm:px-8">
            <OperationError class="mb-4" />
            <div class="flex items-center justify-between gap-x-6">
              <a
                href={routeCancel.url({})}
                data-name="cancel"
                class="text-sm font-semibold leading-6 text-onBackground hover:underline"
              >
                <i18n.Translate>Cancel</i18n.Translate>
              </a>
              <AsyncButton
                submit
                name="create"
                class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                disabled={!submitData || !token}
                onClick={() => create.run(token!, submitData!)}
              >
                <i18n.Translate>Create class</i18n.Translate>
              </AsyncButton>
            </div>
          </div>
        </ConversionRateClassForm>
      </div>
    </section>
  );
}
